import React from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface HotelDescriptionProps {
  description: string;
  collapsedLineClamp?: number;
}

export function HotelDescription({ description, collapsedLineClamp = 4 }: HotelDescriptionProps) {
  const { t } = useTranslation();
  const descriptionId = React.useId();
  const [expanded, setExpanded] = React.useState(false);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const textRef = React.useRef<HTMLParagraphElement | null>(null);

  React.useLayoutEffect(() => {
    const node = textRef.current;
    if (!node) return undefined;

    const measure = () => {
      setIsOverflowing(node.scrollHeight - node.clientHeight > 1);
    };

    if (expanded) return undefined;

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [description, expanded, collapsedLineClamp]);

  const canToggle = isOverflowing || expanded;

  return (
    <div>
      <div className='relative'>
        <p
          className='mt-1 leading-relaxed text-gray-600'
          id={descriptionId}
          ref={textRef}
          style={
            expanded
              ? undefined
              : {
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: collapsedLineClamp,
                  overflow: 'hidden',
                }
          }
        >
          {description}
        </p>

        {!expanded && isOverflowing ? (
          <div
            aria-hidden='true'
            className='pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent'
          />
        ) : null}
      </div>

      {canToggle ? (
        <button
          aria-controls={descriptionId}
          aria-expanded={expanded}
          className='mt-2 inline-flex items-center gap-1 rounded text-sm font-semibold text-blue-600 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          onClick={() => setExpanded((value) => !value)}
          type='button'
        >
          {expanded ? t('hotels.detail.showLess') : t('hotels.detail.showMore')}
          <ExpandMoreIcon
            aria-hidden='true'
            className={`h-4 w-4 transition-transform duration-200 motion-reduce:transition-none ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        </button>
      ) : null}
    </div>
  );
}
